"use client";
import { jsx } from 'react/jsx-runtime';
import { useReducedMotion, useSpring, LayoutGroup, MotionConfig, motion, AnimatePresence } from 'motion/react';
import { getCursorSize } from './get-cursor-size.mjs';
import { useBodyPortal } from './hooks/use-body-portal.mjs';
import { useCursorIsInView } from './hooks/use-cursor-in-view.mjs';
import { useCursorState } from './hooks/use-cursor-state/index.mjs';
import { useHasPointerMoved } from './hooks/use-has-pointer-moved.mjs';
import { useOffset } from './hooks/use-offset.mjs';
import { usePointerPosition } from './hooks/use-pointer-position.mjs';
import { useSnapToTarget } from './hooks/use-snap-to-target.mjs';
import { useCursorStyles } from './use-cursor-styles.mjs';

const topLeftPoint = { x: 0, y: 0 };
const centerPoint = { x: 0.5, y: 0.5 };
const skipTransition = { duration: 0 };
const autoSize = { width: "auto", height: "auto" };
const defaultFollowSpring = { stiffness: 1000, damping: 100 };
function Cursor({ follow = false, center = follow ? topLeftPoint : centerPoint, offset: offsetPoint = topLeftPoint, spring = follow ? defaultFollowSpring : false, snapToTarget = false, matchTextSize = true, children, ...props }) {
    const shouldReduceMotion = useReducedMotion();
    useCursorStyles(!shouldReduceMotion && !follow);
    const position = usePointerPosition();
    const offset = useOffset(position, offsetPoint);
    const springX = useSpring(offset.x, spring || undefined);
    const springY = useSpring(offset.y, spring || undefined);
    const resetSpring = () => {
        const removeX = offset.x.on("change", (v) => {
            springX.jump(v);
            removeX();
        });
        const removeY = offset.y.on("change", (v) => {
            springY.jump(v);
            removeY();
        });
    };
    const state = useCursorState();
    const isInView = useCursorIsInView(resetSpring);
    const { x, y } = useSnapToTarget(spring ? { x: springX, y: springY } : position, snapToTarget, state);
    const { width, height } = children
        ? autoSize
        : getCursorSize(state.type, state, {
            matchPointerSize: snapToTarget,
            matchTextSize,
        });
    // TODO: Set center to 0.5 if state.targetBoundingBox && snapToTarget
    const pointerHasMoved = useHasPointerMoved(position, resetSpring);
    return useBodyPortal(pointerHasMoved ? (jsx(LayoutGroup, { children: jsx(MotionConfig, { transition: shouldReduceMotion
                ? skipTransition
                : props.transition || defaultTransition, children: jsx(motion.div, { layout: true, "data-motion-cursor": follow ? "follow" : "pointer", "data-framer-portal-id": "motion-cursor", initial: "exit", exit: "exit", ...props, variants: {
                    default: { opacity: 1, scale: 1 },
                    exit: { opacity: 0, scale: 0 },
                    pressed: follow ? {} : { scale: 0.9 },
                    ...props.variants,
                }, animate: [
                    "default",
                    state.type,
                    !isInView
                        ? "exit"
                        : state.isPressed
                            ? "pressed"
                            : "",
                ], transformTemplate: positionTransform(center), style: {
                    width,
                    height,
                    borderRadius: follow ? 0 : 20,
                    zIndex: follow ? 99998 : 99999,
                    willChange: "transform",
                    contain: "layout",
                    overflow: "hidden",
                    originX: center.x,
                    originY: center.y,
                    ...props.style,
                    x,
                    y,
                    top: 0,
                    left: 0,
                    position: "fixed",
                    pointerEvents: "none",
                }, children: jsx(AnimatePresence, { children: children }) }) }) })) : null);
}
function positionTransform(origin) {
    return (_, generated) => `translate(-${origin.x * 100}%, -${origin.y * 100}%) ${generated}`;
}
const defaultTransition = { duration: 0.15, ease: [0.38, 0.12, 0.29, 1] };

export { Cursor };
